<script setup lang="ts">
import { inject, nextTick, onMounted, type Ref, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import bus from '@/plugins/bus';
import ProgressSpinner from 'primevue/progressspinner';
import { http } from '@/plugins/axios';
import DialogTemplate from '@/core/components/common/DialogTemplate.vue';
import SocratStudyId from '@/modules/recruitment/components/SocratStudyId.vue';
import type { DynamicDialogInstance } from 'primevue/dynamicdialogoptions';
import { isAxiosError } from 'axios';
import type { Segment } from '@/modules/recruitment/models';

const dialogRef = inject<Ref<DynamicDialogInstance>>('dialogRef');
const toast = useToast();
const studyId = ref();
const study = ref<{
  studyId: number;
  studyTitle: string;
  clientName: string;
  exists: boolean;
} | null>(null);
const loadingStudy = ref(false);
const importingStudies = ref(false);
const studyIdInputRef = ref();

const studyStatus = () => {
  if (!study.value) {
    return '';
  }
  if (study.value.exists) {
    return 'Already imported!';
  }
  return 'Not imported yet';
};

onMounted(() => {
  nextTick(() => {
    studyIdInputRef.value.$el?.focus();
  });
});

const fetchStudy = async (id: string) => {
  try {
    study.value = null;
    loadingStudy.value = true;
    const resp = await http.get('/socrat/studies/' + id);
    if (isAxiosError(resp)) {
      throw resp;
    }
    if (resp.data && resp.data.content) {
      study.value = resp.data.content;
    }
  } catch (error) {
    if (isAxiosError(error) && error.status === 404) {
      toast.add({
        severity: 'error',
        summary: 'Study not found',
        detail: 'Please check that the study exists',
        life: 5000,
      });
      return;
    }
    toast.add({
      severity: 'error',
      summary: 'Fetching study failed',
      detail: error,
      life: 5000,
    });
  } finally {
    loadingStudy.value = false;
  }
};

const onStudySubmit = () => {
  fetchStudy(studyId.value);
};

const onFindClick = () => {
  fetchStudy(studyId.value);
};

const importStudies = async () => {
  if (!study.value) {
    return;
  }

  try {
    importingStudies.value = true;
    const body = { studies: [study.value.studyId] };
    const resp = await http.post('/socrat/studies', body);
    if (isAxiosError(resp)) {
      throw resp;
    }
    toast.add({
      severity: 'success',
      summary: 'Study imported',
      detail: `Study successfully imported`,
      life: 5000,
    });
    if (resp.data && resp.data.content) {
      bus.emit('segments_imported', resp.data.content as Segment[]);
    }

    if (dialogRef) {
      dialogRef.value.close(body);
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Importing study failed',
      detail: error,
      life: 5000,
    });
  } finally {
    setTimeout(() => {
      study.value = null; // Reset
      importingStudies.value = false;
    }, 2000);
  }
};

const onSubmit = () => {
  importStudies();
};
</script>

<template>
  <DialogTemplate @submit.prevent="onSubmit()" title="Import study">
    <Panel header="Import study by ID">
      <form @submit.prevent="onStudySubmit()" class="relative">
        <InputText
          v-model="studyId"
          v-keyfilter.int
          ref="studyIdInputRef"
          :disabled="loadingStudy"
          placeholder="Study ID"
        />
        <Button
          :disabled="loadingStudy || !studyId"
          label="Find"
          icon="pi pi-arrow-right"
          iconPos="right"
          class="ml-5"
          @click="onFindClick()"
        />
        <ProgressSpinner
          style="
            margin-left: 5px;
            width: 40px;
            height: 40px;
            vertical-align: middle;
          "
          v-if="loadingStudy"
        />
      </form>
    </Panel>

    <div v-if="study" class="m-5">
      <dl class="grid grid-cols-[repeat(2,auto)] gap-x-4 w-max">
        <dt class="font-thin">ID</dt>
        <dd class=""><SocratStudyId :id="study.studyId" /></dd>
        <dt class="font-thin">Title</dt>
        <dd class="">{{ study.studyTitle }}</dd>
        <dt class="font-thin">Customer</dt>
        <dd class="">{{ study.clientName }}</dd>
        <dt class="font-thin">Status</dt>
        <dd class="">{{ studyStatus() }}</dd>
      </dl>
    </div>

    <template #footer>
      <div class="flex flex-row justify-end items-center">
        <ProgressSpinner
          class="w-6 h-6 p-0 before:pt-0 m-0 mr-4"
          v-if="importingStudies"
        />
        <Button
          :disabled="importingStudies || !study || study.exists"
          label="Import"
          icon="pi pi-cloud-download"
          @click="importStudies()"
        />
      </div>
    </template>
  </DialogTemplate>
</template>

<style></style>
